<template>
    <div class="create-flow">
        <transition name="sheet" mode="out-in">
        <div v-if="success" key="success" class="sheet success-sheet">
            <div class="status-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4.5 11.2 8.7 15.4 17.5 6.6" stroke="#2AAA4C" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h1>{{ successMode === 'link' ? t('users-create-linked') : t('users-create-success') }}</h1>
            <div class="summary">
                <img v-if="form.logo" :src="form.logo" class="summary-photo" alt="" />
                <span v-else class="summary-photo">{{ initials(form.name) }}</span>
                <strong>{{ form.name }}</strong>
                <div class="pills">
                    <template v-for="(product, index) in successProducts">
                        <span v-if="index > 0" :key="'arrow-' + product" class="arrow">→</span>
                        <span :key="product">{{ productLabel(product) }}</span>
                    </template>
                </div>
                <p v-if="form.username"><img src="images/icons/user-03.svg" alt="" /> {{ form.username }}</p>
                <p v-if="form.email"><img src="images/icons/mail.svg" alt="" /> {{ form.email }}</p>
                <p v-if="form.phone"><img src="images/icons/phone.svg" alt="" /> {{ phoneLabel(form.phone) }}</p>
            </div>
            <p v-if="successMode === 'link'" class="note">{{ t('users-create-linked_note') }}</p>
            <footer class="dock success-dock">
                <button type="button" class="btn-ghost" @click="reset"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.333v9.334M3.333 8h9.334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>{{ t('users-create-another') }}</button>
                <router-link class="btn-green" to="/settings/users">{{ t('users-create-done') }}<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.2 2.6 2.6 6.4-6.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></router-link>
            </footer>
        </div>

        <div v-else key="flow" class="sheet">
            <header class="flow-head">
                <button type="button" class="icon-btn" @click="step === 1 && !mode ? $router.push('/settings/users') : back()" aria-label="Voltar">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4 6 9l5 5" stroke="#383C50" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
                <h1>{{ t('users-create-title') }}</h1>
                <span class="head-spacer" aria-hidden="true"></span>
            </header>
            <div class="stepper">
                <span :class="{ on: step === 1, done: step > 1 }">
                    <img v-if="step === 1" src="images/icons/stepper-on.svg" alt="" />
                    <Icon v-else icon-class="ui-check" color="#2F6BFF" :size="2" />
                    {{ t('users-create-step_user') }}
                </span>
                <span :class="{ on: step === 2, idle: step < 2 }">
                    <img v-if="step === 2" src="images/icons/stepper-on.svg" alt="" />
                    <i v-else class="ring"></i>
                    {{ t('users-create-step_permissions') }}
                </span>
            </div>

            <transition name="pane" mode="out-in">
            <div v-if="step === 1 && !mode" key="search" class="body">
                <label class="field">
                    <span>{{ t('users-create-name') }}</span>
                    <input v-model="query" @input="scheduleSearch" />
                </label>
                <p v-if="searching" class="searching" role="status"><span class="spinner" aria-hidden="true"></span>{{ t('users-create-searching') }}</p>
                <div v-else-if="searchFailed" class="mini-empty">
                    <span class="alert"><img src="images/icons/alert-triangle.svg" alt="" /></span>
                    <strong>{{ t('users-create-failed_title') }}</strong>
                    <p>{{ t('users-create-failed') }}</p>
                </div>
                <p v-else-if="searched && candidates.length" class="found">
                    <img src="images/icons/alert-circle.svg" alt="" />
                    {{ candidates.length === 1 ? t('users-create-found_one') : t('users-create-found', { 0: candidates.length }) }}
                </p>
                <p v-else-if="searched" class="none-chip"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.2 2.6 2.6 6.4-6.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>{{ t('users-create-none') }}</p>

                <article v-for="card in candidates" :key="card.id" class="match">
                    <img v-if="card.logo" :src="card.logo" alt="" />
                    <span v-else class="ph generic"><img src="images/icons/user-03-light.svg" alt="" /></span>
                    <div>
                        <strong>{{ card.name }}</strong>
                        <small v-if="card.email">{{ card.email }}</small>
                    </div>
                    <button type="button" @click="choose(card)">
                        {{ actionLabel(card.kind) }}
                        <em v-if="card.origin_label" :class="'origin-' + card.kind">{{ card.origin_label }}</em>
                    </button>
                </article>

                <button v-if="searched || searchFailed" type="button" class="create-plain" @click="chooseCreate">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.333v9.334M3.333 8h9.334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>{{ t('users-create-create_named', { 0: query }) }}
                </button>

                <p v-if="existing.length" class="existing-label">{{ t('users-create-existing') }}</p>
                <article v-for="card in existing" :key="'e' + card.id" class="match quiet">
                    <img v-if="card.logo" :src="card.logo" alt="" />
                    <span v-else class="ph">{{ initials(card.name) }}</span>
                    <div>
                        <strong>{{ card.name }}</strong>
                        <small v-if="card.email">{{ card.email }}</small>
                    </div>
                    <router-link :to="'/settings/users/' + encodeURIComponent(String(card.id)) + '/profile'">{{ t('users-create-view_profile') }}</router-link>
                </article>
            </div>

            <form v-else-if="step === 1" key="form" class="body" @submit.prevent="goNext">
                <div v-if="mode === 'link'" class="link-banner">
                    <span>{{ t('users-create-linking_label') }}</span>
                    <span class="chip">Kevi Chat</span>
                    <span class="sep" aria-hidden="true">›</span>
                    <span class="chip">Kevi MKT</span>
                </div>
                <div v-else-if="mode === 'import'" class="link-banner is-neutral">
                    <span>{{ t('users-create-importing_label') }}</span>
                    <span v-if="selectedOrigin" class="chip">{{ selectedOrigin }}</span>
                    <span class="sep" aria-hidden="true">›</span>
                    <span class="chip">Kevi MKT</span>
                </div>
                <div v-else-if="mode === 'recover'" class="link-banner is-neutral">
                    <span>{{ t('users-create-recovering_label') }}</span>
                </div>

                <div class="photo">
                    <img v-if="form.logo" :src="form.logo" alt="" />
                    <span v-else class="ph big"><img src="images/icons/user-03-light.svg" alt="" /></span>
                    <button type="button" class="btn-photo" @click="$refs.photo.click()">{{ photoLabel }}</button>
                    <input ref="photo" class="sr" type="file" accept="image/*" @change="uploadPhoto" />
                </div>

                <label v-if="branches.length > 1" class="field">
                    <span>Filial</span>
                    <select v-model="form.places_branches_id">
                        <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.title }}</option>
                    </select>
                </label>

                <kv-switch
                    v-if="isGlobalAdmin"
                    v-model="form.global_admin"
                    :label="form.global_admin ? 'Admin global — Sim' : 'Admin global — Não'"
                />
                <kv-switch
                    v-if="isGlobalAdmin"
                    v-model="form.is_internal"
                    :label="form.is_internal ? 'Interno — Sim' : 'Interno — Não'"
                />
                <label class="field" :class="{ invalid: triedAdvance && !nameOk }">
                    <span>{{ t('users-create-name') }}</span>
                    <input v-model="form.name" required @blur="triedAdvance = true" />
                </label>
                <label class="field" :class="{ invalid: triedAdvance && !usernameOk }">
                    <span>{{ t('users-create-username') }}</span>
                    <input v-model="form.username" required autocomplete="username" :placeholder="t('users-create-username_placeholder')" @input="onUsernameInput" @blur="triedAdvance = true" />
                </label>
                <template v-if="mode === 'create' || mode === 'import'">
                    <label class="field" :class="{ invalid: triedAdvance && !passwordOk }">
                        <span>{{ t('users-create-password') }}</span>
                        <div class="secret-input">
                            <input
                                v-model="form.password"
                                :type="showPassword ? 'text' : 'password'"
                                required
                                minlength="8"
                                placeholder="****************"
                                autocomplete="new-password"
                                @blur="triedAdvance = true"
                            />
                            <button type="button" class="eye-btn" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                    </label>
                    <label class="field" :class="{ invalid: triedAdvance && !passwordMatchOk }">
                        <span>{{ t('users-create-repeat') }}</span>
                        <div class="secret-input">
                            <input
                                v-model="form.password_confirmation"
                                :type="showPasswordRepeat ? 'text' : 'password'"
                                required
                                minlength="8"
                                placeholder="****************"
                                autocomplete="new-password"
                                @blur="triedAdvance = true"
                            />
                            <button type="button" class="eye-btn" @click="showPasswordRepeat = !showPasswordRepeat" :aria-label="showPasswordRepeat ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                    </label>
                </template>
                <label class="field"><span>{{ t('users-create-email') }}</span><input v-model="form.email" type="email" :placeholder="t('users-create-email_placeholder')" /></label>
                <div class="field"><span>{{ t('users-create-phone') }}</span><kv-phone-input v-model="form.phone" :default-country="placeCountry" :placeholder="t('users-create-phone_placeholder')" /></div>

                <div v-if="isGlobalAdmin" class="product-picks">
                    <kv-switch
                        v-model="grantMarketing"
                        :label="grantMarketing ? 'Kevi MKT — Ativar' : 'Kevi MKT — Não ativar'"
                    />
                    <kv-switch
                        v-model="grantChat"
                        :label="grantChat ? 'Kevi Chat — Ativar' : 'Kevi Chat — Não ativar'"
                    />
                </div>
                <div v-if="formError" class="hint warn">
                    {{ formError }}
                    <button v-if="conflict" type="button" class="link-btn" @click="backToSearch">{{ t('users-create-back_to_search') }}</button>
                </div>
                <p v-else-if="triedAdvance && advanceHint" class="hint warn">{{ advanceHint }}</p>
                <footer class="dock">
                    <button type="button" class="btn-ghost" @click="mode = null">{{ t('users-create-back') }}</button>
                    <button type="submit" class="btn-green" :disabled="!canAdvance" :title="!canAdvance ? advanceHint : ''">{{ t('users-create-next') }} →</button>
                </footer>
            </form>

            <form v-else key="permissions" class="body" @submit.prevent="askPassword">
                <div class="perm-head">
                    <img src="images/icons/shield-tick.svg" alt="" />
                    <h2>{{ t('users-create-permissions_title') }}</h2>
                    <p>{{ t('users-create-permissions_subtitle') }}</p>
                </div>
                <permissions-editor :permissions="permissions" />
                <p v-if="formError" class="hint warn">{{ formError }}</p>
                <footer class="dock">
                    <button type="button" class="btn-ghost" @click="step = 1">{{ t('users-create-back') }}</button>
                    <button type="submit" class="btn-green">{{ t('users-create-submit') }}</button>
                </footer>
            </form>
            </transition>
        </div>
        </transition>

        <transition name="overlay">
        <div v-if="noContactOpen" class="overlay" @click.self="noContactOpen = false">
            <div class="dialog" role="dialog">
                <span class="warn-badge"><img src="images/icons/alert-triangle.svg" alt="" /></span>
                <h2>{{ t('users-create-no_contact_title') }}</h2>
                <p>{{ t('users-create-no_contact_body') }}</p>
                <div class="dialog-actions">
                    <button type="button" class="btn-ghost" @click="noContactOpen = false">{{ t('users-create-back') }}</button>
                    <button type="button" class="btn-ghost" @click="noContactOpen = false; step = 2">{{ t('users-create-continue_anyway') }}</button>
                </div>
            </div>
        </div>
        </transition>
        <transition name="overlay">
        <div v-if="passwordOpen" class="overlay" @click.self="passwordOpen = false">
            <div class="dialog" role="dialog">
                <h2>{{ t('users-password_modal-title') }}</h2>
                <p>{{ t('users-password_modal-subtitle') }}</p>
                <label class="field">
                    <span>{{ t('users-password_modal-label') }}</span>
                    <input v-model="adminPassword" type="password" />
                </label>
                <p v-if="formError" class="hint warn">{{ formError }}</p>
                <div class="dialog-actions">
                    <button type="button" class="btn-ghost" :disabled="passwordSubmitting" @click="passwordOpen = false">{{ t('users-password_modal-close') }}</button>
                    <button type="button" class="btn-green" :disabled="passwordSubmitting || !(adminPassword || '').trim()" @click="submit">
                        <span v-if="passwordSubmitting" class="fa fa-spinner fa-spin" aria-hidden="true"></span>
                        {{ passwordSubmitting ? t('users-password_modal-submitting') : t('users-password_modal-continue') }}
                    </button>
                </div>
            </div>
        </div>
        </transition>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import Icon from "../components/icon/icon.vue";
import PermissionsEditor from "./permissions-editor.vue";
import { uploadFilesToStorage } from "../utils/upload-helper";
import KvSwitch from "../components/inputs/kv-switch.vue";
import { formatPhoneNational, fromStoredDigits } from "../helpers/phone";

export default {
    name: "user-wizard",
    components: { Icon, PermissionsEditor, "kv-switch": KvSwitch },
    data() {
        return {
            query: "",
            searching: false,
            searched: false,
            searchFailed: false,
            candidates: [],
            existing: [],
            mode: null,
            step: 1,
            selectedId: null,
            form: {
                name: "",
                username: "",
                email: "",
                phone: "",
                password: "",
                password_confirmation: "",
                logo: null,
                places_branches_id: null,
                global_admin: false,
                is_internal: false,
            },
            permissions: {
                "account.users.manage": { enabled: false, options: {} },
            },
            adminPassword: "",
            searchRequest: 0,
            selectedOrigin: "",
            conflict: null,
            successProducts: [],
            passwordSubmitting: false,
            formError: "",
            success: false,
            successMode: "create",
            noContactOpen: false,
            passwordOpen: false,
            showPassword: false,
            showPasswordRepeat: false,
            triedAdvance: false,
            // Admin global pode escolher em quais produtos vincular.
            grantMarketing: true,
            grantChat: false,
            timer: null,
        };
    },
    computed: {
        placeCountry() {
            const place = this.$store && this.$store.state ? this.$store.state.place : null;
            return place && place.country_code ? place.country_code : "BR";
        },
        branches() {
            const branches = (this.$store.state.place && this.$store.state.place.branches) || [];
            return (branches || []).filter((b) => Number(b.active ?? 1) === 1);
        },
        isGlobalAdmin() {
            const user = this.$store.state.user || {};
            // Alguns fluxos antigos guardam o id em `users_id` e/ou `id` dependendo do endpoint.
            // Para o wizard, consideramos "admin global" apenas quando o flag existe.
            // (Mantém o comportamento esperado e evita liberar selects para usuários comuns.)
            return (user.global_admin === true || user.global_admin === 1);
        },
        photoLabel() {
            if (this.mode === "link") return this.t("users-create-photo_edit");
            if (this.form.logo) return this.t("users-create-photo_change");
            return this.t("users-create-photo_add");
        },
        nameOk() {
            return (this.form.name || "").trim().length >= 3;
        },
        usernameOk() {
            return (this.stripUsernameWhitespace(this.form.username || "") || "").trim().length >= 3;
        },
        passwordOk() {
            if (this.mode !== "create" && this.mode !== "import") return true;
            return (this.form.password || "").length >= 8;
        },
        passwordMatchOk() {
            if (this.mode !== "create" && this.mode !== "import") return true;
            const password = this.form.password || "";
            return password.length >= 8 && password === (this.form.password_confirmation || "");
        },
        canAdvance() {
            if (this.mode === "link" || this.mode === "recover") return this.nameOk && this.usernameOk;
            return this.nameOk && this.usernameOk && this.passwordOk && this.passwordMatchOk;
        },
        advanceHint() {
            if (this.canAdvance) return "";
            if (!this.nameOk) return this.t("users-create-need_name");
            if (!this.usernameOk) return this.t("users-create-need_username");
            if (!this.passwordOk) return this.t("users-create-need_password");
            if (!this.passwordMatchOk) return this.t("users-create-need_password_match");
            return "";
        },
    },
    created() {
        if (!this.canManage()) {
            this.$router.replace("/dashboard");
            return;
        }
        this.$http(keviApi.getOptions("place/team-users/meta")).then((response) => {
            const body = response.body || {};
            const permissions = {};
            (body.permissions || []).forEach((item) => {
                permissions[item.name] = { enabled: 0, options: {} };
            });
            this.permissions = permissions;
        });

        // Defaults: primeira filial ativa (se existir).
        if (this.branches && this.branches.length && !this.form.places_branches_id) {
            this.form.places_branches_id = this.branches[0].id;
        }
    },
    methods: {
        phoneLabel(value) {
            return formatPhoneNational(value, this.placeCountry);
        },
        stripUsernameWhitespace(value) {
            return String(value || "").replace(/\s+/g, "");
        },
        onUsernameInput() {
            const stripped = this.stripUsernameWhitespace(this.form.username || "");
            if (stripped !== (this.form.username || "")) {
                this.form.username = stripped;
            }
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        productLabel(product) {
            return product === "chat" ? "Kevi Chat" : "Kevi MKT";
        },
        canManage() {
            const user = this.$store.state.user || {};
            const permission = user.permissions && user.permissions["account.users.manage"];
            return !!(user.global_admin || (permission && permission.enabled));
        },
        emptyForm(overrides) {
            return {
                name: "",
                username: "",
                email: "",
                phone: "",
                password: "",
                password_confirmation: "",
                logo: null,
                global_admin: false,
                is_internal: false,
                ...overrides,
            };
        },
        scheduleSearch() {
            clearTimeout(this.timer);
            this.timer = setTimeout(this.search, 400);
        },
        search() {
            // Cada busca ganha um número; respostas de buscas antigas são descartadas.
            const request = ++this.searchRequest;
            if (this.query.trim().length < 2) {
                this.searching = false;
                this.searched = false;
                this.candidates = [];
                this.existing = [];
                return;
            }
            this.searching = true;
            this.searchFailed = false;
            this.$http(keviApi.getOptions("place/team-users/search", "POST", { name: this.query.trim() })).then(
                (response) => {
                    if (request !== this.searchRequest) return;
                    this.searching = false;
                    this.searched = true;
                    if (response.body.status === "failed") {
                        this.searchFailed = true;
                        this.candidates = [];
                        this.existing = [];
                        return;
                    }
                    this.candidates = response.body.candidates || [];
                    this.existing = response.body.existing || [];
                },
                () => {
                    if (request !== this.searchRequest) return;
                    this.searching = false;
                    this.searched = true;
                    this.searchFailed = true;
                }
            );
        },
        actionLabel(kind) {
            if (kind === "link") return this.t("users-create-link");
            if (kind === "import") return this.t("users-create-import");
            return this.t("users-create-recover");
        },
        choose(card) {
            this.mode = card.kind;
            this.selectedId = String(card.id);
            this.selectedOrigin = card.origin_label || "";
            this.conflict = null;
            this.formError = "";
            this.triedAdvance = false;
            this.showPassword = false;
            this.showPasswordRepeat = false;
            // Defaults: no MKT, manter Kevi MKT sempre ligado; admin global pode optar por Chat também.
            this.grantMarketing = true;
            this.grantChat = this.isGlobalAdmin ? true : false;
            this.form = this.emptyForm({
                name: card.name || "",
                username: card.username || "",
                email: card.email || "",
                phone: fromStoredDigits(card.phone) || "",
                logo: card.logo || null,
            });
        },
        chooseCreate() {
            this.mode = "create";
            this.selectedId = null;
            this.selectedOrigin = "";
            this.conflict = null;
            this.formError = "";
            this.grantMarketing = true;
            this.grantChat = false;
            this.form = this.emptyForm({ name: this.query.trim() });
            this.triedAdvance = false;
            this.showPassword = false;
            this.showPasswordRepeat = false;
        },
        async uploadPhoto(event) {
            const input = event.target;
            const file = input.files && input.files[0];
            if (!file) return;
            try {
                const urls = await uploadFilesToStorage([file], this.t.bind(this));
                if (urls && urls[0]) this.form.logo = urls[0];
            } catch (err) {
                this.$store.dispatch("notification/warning", (err && err.message) || this.t("users-profile-photo_failed"));
            } finally {
                if (input) input.value = "";
            }
        },
        goNext() {
            this.triedAdvance = true;
            if (!this.canAdvance) return;
            if (!this.form.email && !this.form.phone) {
                this.noContactOpen = true;
                return;
            }
            this.step = 2;
        },
        back() {
            if (this.step === 2) {
                this.step = 1;
                return;
            }
            this.mode = null;
        },
        initials(name) {
            return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
        },
        askPassword() {
            this.formError = "";
            this.passwordSubmitting = false;
            if ((this.mode === "create" || this.mode === "import") && this.form.password !== this.form.password_confirmation) {
                this.formError = this.t("users-profile-mismatch");
                this.step = 1;
                return;
            }
            this.adminPassword = "";
            this.passwordOpen = true;
        },
        async submit() {
            if (this.passwordSubmitting) return;
            const endpoint = {
                create: "place/team-users",
                link: "place/team-users/link",
                recover: "place/team-users/recover",
                import: "place/team-users/import",
            }[this.mode];
            // Admin global e interno só valem para admin global; para os demais nem vão no request.
            const { global_admin, is_internal, ...commonForm } = this.form;
            const basePayload = {
                ...commonForm,
                ...(this.isGlobalAdmin
                    ? { global_admin: global_admin ? 1 : 0, is_internal: is_internal ? 1 : 0 }
                    : {}),
                user_id: this.selectedId,
                permissions: this.permissions,
                admin_password: this.adminPassword,
            };
            this.passwordSubmitting = true;
            this.formError = "";
            try {
                // Admin global pode escolher em quais produtos dar acesso.
                // Para manter compatibilidade e evitar efeitos colaterais, sempre executamos a operação principal
                // em um produto e, se precisar do segundo, fazemos um `link` extra para habilitar o outro.
                const wants = this.isGlobalAdmin
                    ? {
                          marketing: !!this.grantMarketing,
                          chat: !!this.grantChat,
                      }
                    : { marketing: true, chat: false };

                if (!wants.marketing && !wants.chat) {
                    this.formError = "Selecione pelo menos um produto.";
                    this.passwordSubmitting = false;
                    return;
                }

                const primaryProduct = wants.marketing ? "marketing" : "chat";
                const secondaryProduct = wants.marketing && wants.chat ? (primaryProduct === "marketing" ? "chat" : "marketing") : null;

                const primaryOptions = keviApi.getOptions(endpoint, "POST", { ...basePayload, product: primaryProduct });
                const primaryResponse = await this.$http(primaryOptions);

                // id do usuário retornado pela API (Marketing users_id).
                const savedId = String((primaryResponse && primaryResponse.body && primaryResponse.body.id) || this.selectedId || "");

                let finalResponse = primaryResponse;
                if (secondaryProduct && savedId) {
                    const linkOptions = keviApi.getOptions("place/team-users/link", "POST", {
                        ...basePayload,
                        user_id: savedId,
                        product: secondaryProduct,
                    });
                    finalResponse = await this.$http(linkOptions);
                }

                this.passwordSubmitting = false;
                this.passwordOpen = false;
                this.successMode = (finalResponse.body && finalResponse.body.mode) || this.mode;
                this.successProducts = this.productsFrom(finalResponse.body);
                this.success = true;
            } catch (response) {
                this.passwordSubmitting = false;
                const body = (response && response.body) || {};
                const message = body.message;
                // Mensagens "humanas" (ex.: limite de usuários) devem aparecer direto na UI.
                if (response && response.status === 409 && typeof message === "string" && message.trim().length > 10) {
                    this.formError = message;
                    return;
                }
                if (message === "wrong_password") {
                    this.formError = this.t("users-password_modal-wrong");
                    return;
                }
                if (message === "too_many_attempts" || response.status === 429) {
                    this.formError = this.t("users-password_modal-too_many");
                    return;
                }
                if (message === "mismatch") {
                    this.passwordOpen = false;
                    this.step = 1;
                    this.formError = this.t("users-profile-mismatch");
                    return;
                }
                if (message === "weak_password") {
                    this.passwordOpen = false;
                    this.step = 1;
                    this.formError = this.t("users-profile-password_rule");
                    return;
                }
                if (message === "taken" || (response.status === 409 && body.conflict)) {
                    this.conflict = body.conflict || null;
                    this.formError = this.t("users-create-taken", { 0: (body.conflict && body.conflict.name) || "" });
                    this.passwordOpen = false;
                    this.step = 1;
                    return;
                }
                if (message === "invalid") {
                    this.passwordOpen = false;
                    this.step = 1;
                    this.formError = this.invalidText(body.errors);
                    return;
                }
                this.formError = this.t("users-password_modal-failed");
            }
        },
        invalidText(errors) {
            const labels = {
                name: "users-create-name",
                username: "users-create-username",
                email: "users-create-email",
                password: "users-create-password",
                phone: "users-create-phone",
            };
            const reasons = ["empty", "small", "invalid", "taken"];
            const parts = Object.keys(errors || {}).filter((key) => errors[key]).map((key) => {
                const label = labels[key] ? this.t(labels[key]) : key;
                const reason = reasons.includes(errors[key]) ? this.t("users-create-reason_" + errors[key]) : errors[key];
                return label + " " + reason;
            });
            return parts.length ? parts.join(". ") + "." : this.t("users-password_modal-failed");
        },
        productsFrom(body) {
            const ids = (body && body.kevi_product_ids) || null;
            if (Array.isArray(ids) && ids.length) {
                // Ordem do vínculo: produto de origem primeiro, Kevi MKT por último.
                const products = [];
                if (ids.map(Number).includes(2)) products.push("chat");
                if (ids.map(Number).includes(1)) products.push("marketing");
                if (products.length) return products;
            }
            return this.successMode === "link" ? ["chat", "marketing"] : ["marketing"];
        },
        backToSearch() {
            const conflict = this.conflict;
            this.conflict = null;
            this.formError = "";
            this.mode = null;
            this.step = 1;
            this.query = (conflict && (conflict.name || conflict.username)) || this.form.name || this.query;
            this.search();
        },
        reset() {
            this.success = false;
            this.selectedOrigin = "";
            this.conflict = null;
            this.successProducts = [];
            this.mode = null;
            this.step = 1;
            this.query = "";
            this.form = this.emptyForm();
            this.candidates = [];
            this.existing = [];
            this.searched = false;
            this.triedAdvance = false;
            this.showPassword = false;
            this.showPasswordRepeat = false;
            this.formError = "";
        },
    },
};
</script>

<style scoped>
.create-flow {
    height: calc(100dvh - 88px);
    background: #f7f7f8;
    display: flex;
    justify-content: center;
    align-items: stretch;
    margin: -20px -20px 0;
    padding: 16px 24px;
    box-sizing: border-box;
    overflow: hidden;
    font-family: "Inter", sans-serif;
    animation: rise .28s cubic-bezier(.16, 1, .3, 1);
}
@keyframes rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.sheet { width: min(640px, 100%); height: 100%; min-height: 0; background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; }
.head-spacer { width: 36px; height: 36px; }
.flow-head { display: grid; grid-template-columns: 36px 1fr 36px; align-items: center; height: 36px; margin: 12px 12px 8px; }
.flow-head h1, .success-sheet h1 { margin: 0; text-align: center; font-family: "Sora", sans-serif; font-size: 16px; font-weight: 500; line-height: 1.3; color: #626473; }
.icon-btn { width: 36px; height: 36px; border: 0; background: transparent; cursor: pointer; border-radius: 8px; }
.icon-btn:hover { background: #f3f3f6; }
.stepper { display: flex; width: calc(100% - 16px); margin: 0 auto; }
.stepper span {
    display: flex; align-items: center; justify-content: center; gap: 4px;
    flex: 1; height: 30px; padding: 8px; box-sizing: border-box;
    border: 1px solid #f3f3f6; background: #fff;
    font-size: 12px; font-weight: 500; line-height: 1.2; color: #d2d3db;
}
.stepper img { width: 14px; height: 14px; display: block; }
.stepper span:first-child { border-radius: 8px 0 0 8px; }
.stepper span:last-child { border-radius: 0 8px 8px 0; }
.stepper span.on { background: #f3f3f6; color: #383c50; border-color: #f3f3f6; }
.stepper span.done { background: #eef3ff; color: #2f6bff; }
.dot, .ring { width: 14px; height: 14px; border-radius: 50%; display: inline-block; box-sizing: border-box; }
.dot { background: #2f6bff; box-shadow: inset 0 0 0 3px #f3f3f6, 0 0 0 2px #2f6bff; }
.stepper span.on .dot { box-shadow: inset 0 0 0 3px #f3f3f6, 0 0 0 2px #2f6bff; background: #2f6bff; }
.ring { border: 1.5px solid #d2d3db; }
.body { display: flex; flex-direction: column; gap: 16px; padding: 24px 16px 0; flex: 1; min-height: 0; overflow: auto; }
.body .dock { margin: auto -16px 0; position: sticky; bottom: 0; z-index: 2; }
.field { display: flex; flex-direction: column; gap: 8px; margin: 0; }
.field span, .body h2, .body h3 { font-size: 14px; font-weight: 500; color: #383c50; margin: 0; }
.body h2 { margin-top: 8px; }
.product-picks {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    background: #fafafc;
}
.field input,
.field select {
    box-sizing: border-box;
    display: block;
    width: 100%;
    height: 44px;
    margin: 0;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-shadow: none;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    color: #1f2337;
}
.field input::placeholder { color: #9598a6; font-size: 12px; }
.field input:focus,
.field select:focus { outline: none; border-color: #bbbdc7; background: #fff; }
.mini-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 32px 16px; text-align: center; }
.mini-empty span { width: 40px; height: 40px; border-radius: 50%; background: #edfcf3; display: flex; align-items: center; justify-content: center; }
.mini-empty span.alert { background: #fffbed; }
.mini-empty img { width: 24px; height: 24px; }
.mini-empty strong { font-family: "Sora", sans-serif; font-size: 16px; font-weight: 400; color: #1f2337; }
.mini-empty p { margin: 0; max-width: 280px; font-size: 14px; line-height: 1.35; color: #626473; }
.hint { margin: 0; color: #9598a6; font-size: 13px; }
.hint.warn { color: #b54708; }
.searching { margin: 0; display: flex; align-items: center; justify-content: center; gap: 8px; color: #626473; font-size: 13px; }
.spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #d9e5ff; border-top-color: #2f6bff; animation: spin .7s linear infinite; flex: none; }
@keyframes spin { to { transform: rotate(360deg); } }
.none-chip { margin: 0 auto; display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 999px; background: #edfcf3; color: #2aaa4c; font-size: 12px; line-height: 1.3; }
.existing-label { margin: 8px 0 0; text-align: center; color: #9598a6; font-size: 12px; font-weight: 500; }
.ph.generic { border: 1px dashed #d2d3db; box-sizing: border-box; }
.ph.generic img { width: 20px; height: 20px; }
.match em.origin-import { background: #f3f3f6; color: #626473; }
.link-btn { border: 0; background: none; padding: 0; margin-left: 4px; color: #2aaa4c; font: inherit; text-decoration: underline; cursor: pointer; }
.found { margin: 0; color: #e07a12; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 13px; font-weight: 600; }
.found img { width: 16px; height: 16px; }
.match { background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 16px; display: grid; grid-template-columns: 44px minmax(0, 1fr); gap: 8px 12px; min-width: 0; }
.match > div { min-width: 0; }
.match img, .ph { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; background: #f3f3f6; display: flex; align-items: center; justify-content: center; color: #626473; font-size: 14px; }
.match strong, .match small { display: block; overflow-wrap: anywhere; word-break: break-word; }
.match strong { font-family: "Sora", sans-serif; font-size: 16px; font-weight: 500; color: #1f2337; }
.match small { color: #9598a6; font-size: 13px; line-height: 1.3; }
.match button, .match a { grid-column: 1 / -1; height: 40px; border-radius: 8px; border: 1px solid #e5e6eb; background: #fafafc; display: flex; align-items: center; justify-content: center; gap: 8px; color: #383c50; text-decoration: none; font-size: 14px; cursor: pointer; }
.match em { font-style: normal; background: #e7f8ec; color: #178a3a; border-radius: 999px; padding: 2px 8px; font-size: 12px; }
.create-plain {
    box-sizing: border-box;
    min-height: 48px;
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid #e5e6eb;
    background: #fff;
    font-size: 14px;
    line-height: 1.3;
    color: #383c50;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 16px;
}
.link-banner { margin: -24px -16px 0; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px; background: #f1fcf3; color: #209941; padding: 10px 16px; font-size: 13px; font-weight: 500; }
.link-banner.is-neutral { background: #f9f9fb; color: #626473; }
.link-banner .chip { padding: 2px 8px; border-radius: 999px; background: #defae5; color: #209941; font-size: 12px; }
.link-banner.is-neutral .chip { background: #f3f3f6; color: #626473; }
.link-banner .sep { color: inherit; }
.photo { display: flex; flex-direction: column; align-items: center; gap: 8px; margin-bottom: 4px; }
.photo > img, .ph.big { width: 124px; height: 124px; border-radius: 50%; object-fit: cover; background: #f3f3f6; display: flex; align-items: center; justify-content: center; }
.ph.big img { width: 48px; height: 48px; opacity: 0.85; }
.btn-photo { height: 35px; padding: 0 12px; border-radius: 8px; border: 1px solid #e5e6eb; background: #fafafc; color: #383c50; font-size: 14px; cursor: pointer; }
.sr { display: none; }
.secret-input {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 44px;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-sizing: border-box;
}
.secret-input:focus-within { border-color: #bbbdc7; background: #fff; }
.secret-input input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
}
.secret-input input:focus { outline: none; border: 0; background: transparent; }
.eye-btn {
    border: 0;
    background: transparent;
    padding: 0;
    width: 20px;
    height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
}
.eye-btn img { width: 20px; height: 20px; display: block; }
.field.invalid .secret-input,
.field.invalid > input {
    border-color: #fecdca;
    background: #fef3f2;
}
.perm-head { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px; margin-bottom: 8px; }
.perm-head img { width: 28px; height: 28px; }
.perm-head h2 { font-family: "Sora", sans-serif; font-size: 18px; font-weight: 500; color: #1f2337; }
.perm-head p { margin: 0; max-width: 360px; color: #626473; font-size: 14px; font-weight: 400; }
.switch-row { display: flex; justify-content: space-between; gap: 16px; align-items: center; background: #fff; border: 1px solid #e5e6eb; border-radius: 12px; padding: 14px 16px; }
.switch-row strong { display: block; font-size: 14px; color: #1f2337; }
.switch-row small { display: block; color: #9598a6; font-weight: 400; font-size: 12px; margin-top: 4px; }
.switch { position: relative; width: 36px; height: 20px; flex: 0 0 36px; }
.switch input { position: absolute; opacity: 0; width: 0; height: 0; margin: 0; }
.switch i { position: absolute; inset: 0; background: #e5e6eb; border-radius: 999px; transition: background .15s; }
.switch i:before { content: ""; position: absolute; width: 16px; height: 16px; left: 2px; top: 2px; background: #fff; border-radius: 50%; transition: transform .15s; box-shadow: 0 1px 2px rgba(16,24,40,.12); }
.switch input:checked + i { background: #2aaa4c; }
.switch input:checked + i:before { transform: translateX(16px); }
.dock {
    position: sticky;
    bottom: 0;
    display: flex;
    gap: 8px;
    margin: 0;
    padding: 16px 24px 24px;
    border-top: 1px solid #e5e6eb;
    background: #fafafc;
    width: 100%;
    box-sizing: border-box;
    flex: none;
}
.btn-ghost, .btn-green { flex: 1; box-sizing: border-box; padding: 12px; border-radius: 8px; font-size: 16px; font-weight: 400; line-height: 1.2; text-align: center; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; }
.btn-ghost { border: 1px solid #e5e6eb; background: #fafafc; color: #383c50; }
.btn-green { border: 0; background: #2aaa4c; color: #f1fcf3; gap: 8px; }
.btn-green:disabled { background: #f3f3f6; color: #9598a6; cursor: default; }
.stepper .ui-icon { display: block; flex: none; }
.success-sheet {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px 24px 0;
    min-height: 0;
    height: 100%;
    box-sizing: border-box;
}
.success-sheet > h1,
.success-sheet > .status-icon,
.success-sheet > .summary,
.success-sheet > .note {
    flex: none;
}
.success-dock {
    position: static;
    margin-top: auto;
    width: 100%;
    max-width: 360px;
    border-top: 0;
    background: transparent;
    padding: 24px 0 32px;
}
.status-icon { width: 48px; height: 48px; border-radius: 50%; background: #e8f8ec; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
.summary { margin-top: 24px; width: min(360px, 100%); border: 1px solid #f3f3f6; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 6px; background: #f9f9fb; box-shadow: 0 8px 12px rgba(0, 0, 0, 0.06); box-sizing: border-box; }
.summary-photo { width: 84px; height: 84px; border-radius: 50%; object-fit: cover; background: #f3f3f6; display: flex; align-items: center; justify-content: center; }
.summary strong { font-family: "Sora", sans-serif; font-size: 18px; font-weight: 500; color: #1f2337; }
.summary p { margin: 0; display: flex; align-items: center; gap: 6px; color: #626473; font-size: 13px; overflow-wrap: anywhere; word-break: break-word; }
.summary p img { width: 16px; height: 16px; }
.pills { display: flex; gap: 6px; align-items: center; }
.pills span { background: #e7f8ec; color: #178a3a; border-radius: 999px; padding: 2px 8px; font-size: 12px; }
.pills .arrow { background: transparent; color: #178a3a; padding: 0; }
.note { color: #626473; font-size: 13px; max-width: 320px; }
.overlay { position: fixed; inset: 0; background: rgba(31, 35, 55, .35); display: flex; align-items: center; justify-content: center; z-index: 40; padding: 16px; }
.dialog { width: min(343px, 100%); background: #fff; border-radius: 16px; padding: 16px; display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; box-shadow: 0 16px 40px rgba(16,24,40,.16); }
.dialog h2 { margin: 0; font-family: "Sora", sans-serif; font-size: 20px; font-weight: 400; color: #1f2337; line-height: 1.3; }
.dialog p { margin: 0; color: #626473; font-size: 14px; }
.dialog .field { width: 100%; text-align: left; }
.warn-badge { width: 40px; height: 40px; border-radius: 50%; background: #fffbed; display: flex; align-items: center; justify-content: center; }
.warn-badge img { width: 24px; height: 24px; }
.dialog-actions { display: flex; gap: 8px; width: 100%; }
.dialog-actions .btn-ghost, .dialog-actions .btn-green { flex: 1; min-height: 43px; }
.sheet-enter-active, .sheet-leave-active, .pane-enter-active, .pane-leave-active { transition: opacity .22s ease, transform .24s cubic-bezier(.16, 1, .3, 1); }
.sheet-enter, .pane-enter { opacity: 0; transform: translateY(10px); }
.sheet-leave-to, .pane-leave-to { opacity: 0; transform: translateY(-8px); }
.overlay-enter-active, .overlay-leave-active { transition: opacity .2s ease; }
.overlay-enter, .overlay-leave-to { opacity: 0; }
.dialog { transition: transform .24s cubic-bezier(.16, 1, .3, 1), opacity .2s ease; }
.overlay-enter .dialog, .overlay-leave-to .dialog { opacity: 0; transform: translateY(12px) scale(.98); }
@media (max-width: 700px) {
    .create-flow { height: auto; min-height: calc(100dvh - 50px); margin: 0; padding: 0; overflow: visible; background: #fff; }
    .sheet { width: 100%; height: auto; min-height: calc(100dvh - 50px); display: flex; flex-direction: column; border: 0; border-radius: 0; }
    .flow-head { margin: 8px 8px 4px; }
    .flow-head h1 { font-size: 16px; }
    .stepper { width: calc(100% - 16px); }
    .stepper span { min-width: 0; padding: 8px 4px; white-space: nowrap; }
    .body { flex: 1; padding-bottom: 108px; }
    .dock, .body .dock {
        position: fixed; left: 0; right: 0; bottom: 0; z-index: 6; margin: 0;
        padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
        max-width: none;
    }
    .success-sheet {
        justify-content: center;
        padding: 24px 16px 120px;
        min-height: calc(100dvh - 50px);
        height: auto;
    }
    .success-dock {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        max-width: none;
        width: auto;
        border-top: 1px solid #e5e6eb;
        background: #fafafc;
        padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
        margin-top: 0;
    }
    .match button, .match a { min-width: 0; flex-wrap: wrap; height: auto; min-height: 40px; padding: 8px; }
    .dialog { width: calc(100% - 32px); }
}
@media (min-width: 701px) {
    .success-sheet .dock.success-dock {
        position: static;
        left: auto;
        right: auto;
        bottom: auto;
    }
}
</style>
